import React, { useState } from "react";
import {
  Page,
  Card,
  Button,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Checkbox,
} from "@shopify/polaris";
import { runTierMigration, runRetroactiveGrant, setProgramEnabled } from "../lib/api.js";

const STEPS = [
  { id: "migrate", title: "1. Tier İsim Migration" },
  { id: "retroactive", title: "2. Retroactive Puan Dağıtımı" },
  { id: "enable", title: "3. Programı Aktifleştir" },
  { id: "smoke", title: "4. Manuel Smoke Test" },
];

const SMOKE_LABELS = {
  welcome: "Yeni hesap aç → 50 HP welcome bonus geldi",
  orderPaid: "Test sipariş ver → puan otomatik düştü (5 dk içinde)",
  dashboard: "/account sayfasında dashboard'da puan ve tier doğru gösteriliyor",
  spin: "Çark çevir → puan eklendi, 'yarın tekrar' kilidi açıldı",
  coupon: "100 HP ile kupon bas → discount code üretildi",
  cart: "Kuponu sepette dene → min sepet kontrolü ve indirim çalışıyor",
};

function smokeChecksInitial() {
  return {
    welcome: false,
    orderPaid: false,
    dashboard: false,
    spin: false,
    coupon: false,
    cart: false,
  };
}

function allSmokeChecksDone(checks) {
  return Object.values(checks).every(Boolean);
}

function StepBadge({ status }) {
  if (status === "done") return <Badge tone="success">Tamamlandı</Badge>;
  if (status === "running") return <Badge tone="info">Çalışıyor</Badge>;
  if (status === "error") return <Badge tone="critical">Hata</Badge>;
  return <Badge>Beklemede</Badge>;
}

function ResultSummary({ status, successKeys }) {
  if (status.state === "done" && status.result) {
    return (
      <Banner tone="success" title="Başarılı">
        <BlockStack gap="050">
          {successKeys.map((k) => (
            <Text key={k} as="p">
              <strong>{k}:</strong> {String(status.result[k])}
            </Text>
          ))}
          {status.result.errors?.length > 0 && (
            <Text as="p" tone="critical">
              {status.result.errors.length} hata: {JSON.stringify(status.result.errors).slice(0, 200)}
            </Text>
          )}
        </BlockStack>
      </Banner>
    );
  }
  if (status.state === "error") {
    return <Banner tone="critical" title="Hata">{status.error}</Banner>;
  }
  return null;
}

export default function LaunchWizard() {
  const [stepStatus, setStepStatus] = useState({
    migrate: { state: "idle", result: null, error: null },
    retroactive: { state: "idle", result: null, error: null },
    enable: { state: "idle", result: null, error: null },
    smoke: { state: "idle", checks: smokeChecksInitial() },
  });

  function update(stepId, partial) {
    setStepStatus((s) => ({ ...s, [stepId]: { ...s[stepId], ...partial } }));
  }

  async function runStep(stepId, fn) {
    update(stepId, { state: "running", error: null });
    try {
      const result = await fn();
      update(stepId, { state: "done", result });
    } catch (err) {
      update(stepId, { state: "error", error: err.message });
    }
  }

  function isStepEnabled(idx) {
    if (idx === 0) return true;
    const prevId = STEPS[idx - 1].id;
    if (prevId === "smoke") return false;
    return stepStatus[prevId].state === "done";
  }

  return (
    <Page title="🚀 Lansman Sihirbazı" subtitle="BO VIP Kulüp programını canlıya alma adımları">
      <BlockStack gap="400">
        <Banner tone="info">
          Bu sihirbaz, programı canlıya almadan önce gerekli tüm migration ve aktivasyon adımlarını sırayla yürütür. Her adım idempotent: tekrar çalıştırmak güvenlidir.
        </Banner>

        <Card>
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text variant="headingMd" as="h3">{STEPS[0].title}</Text>
              <StepBadge status={stepStatus.migrate.state} />
            </InlineStack>
            <Text as="p" tone="subdued">
              Eski tier isimlerini (bronze/silver/gold) yenileriyle (rookie/pro/legend) değiştirir.
            </Text>
            <Button
              variant="primary"
              loading={stepStatus.migrate.state === "running"}
              disabled={!isStepEnabled(0)}
              onClick={() => runStep("migrate", runTierMigration)}
            >
              Çalıştır
            </Button>
            <ResultSummary status={stepStatus.migrate} successKeys={["scanned", "updated", "skipped"]} />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text variant="headingMd" as="h3">{STEPS[1].title}</Text>
              <StepBadge status={stepStatus.retroactive.state} />
            </InlineStack>
            <Text as="p" tone="subdued">
              Mevcut müşterilere geçmiş toplam harcamaya göre Hobi Puan ve seviye atar. Halihazırda puanı olan müşterileri atlar.
            </Text>
            <Button
              variant="primary"
              loading={stepStatus.retroactive.state === "running"}
              disabled={!isStepEnabled(1)}
              onClick={() => runStep("retroactive", runRetroactiveGrant)}
            >
              Çalıştır
            </Button>
            <ResultSummary
              status={stepStatus.retroactive}
              successKeys={["scanned", "granted", "skipped", "totalPointsDistributed"]}
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text variant="headingMd" as="h3">{STEPS[2].title}</Text>
              <StepBadge status={stepStatus.enable.state} />
            </InlineStack>
            <Text as="p" tone="subdued">
              `shop.metafields.boloyalty.program_enabled = "true"` olarak ayarlar. Bundan sonra storefront ve webhook'lar programı aktif kabul eder.
            </Text>
            <Button
              variant="primary"
              loading={stepStatus.enable.state === "running"}
              disabled={!isStepEnabled(2)}
              onClick={() => runStep("enable", () => setProgramEnabled(true))}
            >
              Programı Aktifleştir
            </Button>
            <ResultSummary status={stepStatus.enable} successKeys={["success"]} />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <InlineStack align="space-between">
              <Text variant="headingMd" as="h3">{STEPS[3].title}</Text>
              <StepBadge status={allSmokeChecksDone(stepStatus.smoke.checks) ? "done" : "idle"} />
            </InlineStack>
            <Text as="p" tone="subdued">
              Aşağıdaki testleri kendiniz manuel olarak yapın ve tamamlandıkça işaretleyin. Bütün checkbox'lar tıklandığında program canlıya hazırdır.
            </Text>
            <BlockStack gap="100">
              {Object.entries(SMOKE_LABELS).map(([key, label]) => (
                <Checkbox
                  key={key}
                  label={label}
                  checked={stepStatus.smoke.checks[key]}
                  disabled={stepStatus.enable.state !== "done"}
                  onChange={(v) =>
                    update("smoke", {
                      checks: { ...stepStatus.smoke.checks, [key]: v },
                    })
                  }
                />
              ))}
            </BlockStack>
            {allSmokeChecksDone(stepStatus.smoke.checks) && (
              <Banner tone="success" title="Tüm smoke test'ler tamamlandı">
                Faz 1 (sessiz lansman) için artık temayı yayınlayabilir, BO VIP Kulüp sayfasını oluşturabilir ve navigasyon linklerini ekleyebilirsiniz.
              </Banner>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
